import { isEmpty, isNil } from 'lodash';
// Import Types
import type { SchemaDependency, SegmentationCategory } from 'types';
// Import Constants
import { SCHEMA_COLORS } from 'constants/schemaDependencies';

/** Minimal column shape needed to resolve schema dependencies (matches `ColumnInfo` and `FormulaColumn`). */
export interface SchemaDependencyColumn {
    SchemaId: number;
    SchemaName: string;
    Dependencies?: Nullable<SchemaDependencyColumn[]>;
}

export type SchemaColorMap = Record<number, string>;

/**
 * Returns the schema ids a column depends on: the schemas of its `Dependencies`,
 * or its own schema when it has no dependencies.
 */
export const collectColumnSchemaIds = (column: Nullable<SchemaDependencyColumn> | undefined): number[] => {
    if (isNil(column)) return [];

    const source = isEmpty(column.Dependencies) ? [column] : (column.Dependencies as SchemaDependencyColumn[]);

    return source.reduce<number[]>((acc, dep) => {
        if (!isNil(dep?.SchemaId) && !acc.includes(dep.SchemaId)) {
            acc.push(dep.SchemaId);
        }
        return acc;
    }, []);
};

/** Assigns a palette color to each schema id, in the order the ids are given (first seen → first color). */
export const buildSchemaColorMap = (schemaIds: number[]): SchemaColorMap => {
    const uniqueIds = schemaIds.filter((id, index) => schemaIds.indexOf(id) === index);

    return uniqueIds.reduce<SchemaColorMap>((acc, schemaId, index) => {
        acc[schemaId] = SCHEMA_COLORS[index % SCHEMA_COLORS.length];
        return acc;
    }, {});
};

/**
 * Builds the dependency list shown by the dependency dots for one column:
 * one entry per dependent schema (deduplicated, sorted by name), or the column's own schema when it has none.
 */
export const buildSchemaDependencies = (
    column: Nullable<SchemaDependencyColumn> | undefined,
    schemaColorMap: SchemaColorMap,
): SchemaDependency[] => {
    if (isNil(column)) return [];

    const source = isEmpty(column.Dependencies) ? [column] : (column.Dependencies as SchemaDependencyColumn[]);
    const schemaMap = new Map<number, SchemaDependency>();

    source.forEach((dep) => {
        if (isNil(dep?.SchemaId)) return;
        schemaMap.set(dep.SchemaId, {
            id: dep.SchemaId,
            name: dep.SchemaName,
            color: schemaColorMap[dep.SchemaId],
        });
    });

    return Array.from(schemaMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/** Walks the segmentation category tree and collects every filter column's schema ids in first-seen order. */
export const collectSegmentationTreeSchemaIds = (
    categories: Nullable<SegmentationCategory[]> | undefined,
): number[] => {
    const schemaIds: number[] = [];

    const visit = (category: SegmentationCategory) => {
        (category.SegmentationFilters ?? []).forEach((filter) => {
            collectColumnSchemaIds(filter.Column).forEach((schemaId) => {
                if (!schemaIds.includes(schemaId)) {
                    schemaIds.push(schemaId);
                }
            });
        });
        // `SubCategories` is typed as string[] but holds nested categories at runtime.
        (category.SubCategories as unknown as SegmentationCategory[] | undefined)?.forEach(visit);
    };

    (categories ?? []).forEach(visit);

    return schemaIds;
};
